export interface DelimitedRecord {
  /** 貼り付けテキスト内の物理行番号（1始まり）。セル内改行も行数として数える */
  readonly lineNumber: number;
  readonly columns: readonly string[];
}

export interface DelimitedTextParseResult {
  readonly records: readonly DelimitedRecord[];
  readonly delimiter: string;
  /** 閉じられていないクォートを検出した場合 true。以降のテキストが1セルに畳み込まれている可能性がある */
  readonly hasUnterminatedQuote: boolean;
}

/**
 * 区切り文字を推定する。
 *
 * スプレッドシートから直接コピペするとタブ区切り、CSVで書き出したファイルの中身は
 * カンマ区切りになるため、どちらの手元データでもそのまま貼り付けられるようにする。
 * 先頭に空行が混ざっても判定できるよう、最初の中身のある行を見る。
 */
function detectDelimiter(text: string): string {
  const firstContentLine =
    text.split("\n").find((line) => line.trim().length > 0) ?? "";
  const tabCount = firstContentLine.split("\t").length - 1;
  const commaCount = firstContentLine.split(",").length - 1;
  return tabCount > commaCount ? "\t" : ",";
}

// RFC4180 準拠: クォート内の改行・区切り文字を保持し、"" は " に復元する
function splitRecords(
  text: string,
  delimiter: string,
): { records: DelimitedRecord[]; hasUnterminatedQuote: boolean } {
  const records: DelimitedRecord[] = [];
  let columns: string[] = [];
  let field = "";
  let inQuotes = false;
  let lineNumber = 1;
  let recordStartLine = 1;
  let i = 0;

  while (i < text.length) {
    const char = text[i];
    if (char === "\n") {
      lineNumber += 1;
    }
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }
    if (char === '"' && field === "") {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === delimiter) {
      columns.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (char === "\n") {
      columns.push(field);
      field = "";
      records.push({ lineNumber: recordStartLine, columns });
      columns = [];
      recordStartLine = lineNumber;
      i += 1;
      continue;
    }
    field += char;
    i += 1;
  }
  columns.push(field);
  records.push({ lineNumber: recordStartLine, columns });

  return { records, hasUnterminatedQuote: inQuotes };
}

/**
 * 貼り付けられたタブ区切り／カンマ区切りのテキストを行と列に分解する。
 *
 * 列の意味づけ（どの列が何か、どの行を採用するか）は呼び出し側の責務。
 */
export function parseDelimitedText(text: string): DelimitedTextParseResult {
  // CRLF/CR を LF に正規化し、セル内改行にも \r が残らないようにする
  const normalizedText = text.replace(/\r\n?/g, "\n");
  const delimiter = detectDelimiter(normalizedText);
  const { records, hasUnterminatedQuote } = splitRecords(
    normalizedText,
    delimiter,
  );
  return { records, delimiter, hasUnterminatedQuote };
}
